import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { invocarFuncion } from '../lib/invocarFuncion'
import { useWorkspace } from '../lib/WorkspaceContext'
import PanelAdminCRUD from '../components/PanelAdminCRUD'
import './Clases.css'

const TIPO_ICONOS = { video: '🎥', pdf: '📄', audio: '🎧' }
const TIPO_LABELS = { video: 'Video', pdf: 'PDF', audio: 'Audio' }
const FORM_MODULO_VACIO = { titulo: '', descripcion: '' }
const FORM_CONTENIDO_VACIO = { titulo: '', tipo: 'video', url: '', descripcion: '', duracion_min: '' }

const COLUMNAS_MODULOS = [
  { key: 'titulo', label: 'Módulo' },
  { key: 'descripcion', label: 'Descripción', render: m => m.descripcion || '—' }
]

const COLUMNAS_CONTENIDO = [
  { key: 'titulo', label: 'Título', render: item => <>{TIPO_ICONOS[item.tipo]} {item.titulo}</> },
  { key: 'tipo', label: 'Tipo', render: item => TIPO_LABELS[item.tipo] },
  { key: 'duracion', label: 'Duración', render: item => item.duracion_min ? `${item.duracion_min} min` : '—' }
]

export default function Clases({ usuario, esAdmin }) {
  const { workspaceId } = useWorkspace()
  const [modulos, setModulos] = useState([])
  const [moduloActivo, setModuloActivo] = useState(null)
  const [contenido, setContenido] = useState([])
  const [nota, setNota] = useState('')
  const [notaGuardada, setNotaGuardada] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingContenido, setLoadingContenido] = useState(false)
  const autoGuardadoRef = useRef(null)

  // Admin: estado para agregar/editar módulo o contenido
  const [modalModulo, setModalModulo] = useState(false)
  const [modalContenido, setModalContenido] = useState(false)
  const [formModulo, setFormModulo] = useState(FORM_MODULO_VACIO)
  const [formContenido, setFormContenido] = useState(FORM_CONTENIDO_VACIO)
  const [moduloEditando, setModuloEditando] = useState(null)
  const [contenidoEditando, setContenidoEditando] = useState(null)
  const [subiendoArchivo, setSubiendoArchivo] = useState(false)
  const [guardandoModulo, setGuardandoModulo] = useState(false)
  const [guardandoContenido, setGuardandoContenido] = useState(false)
  const [errorModulo, setErrorModulo] = useState(false)
  const [erroresContenido, setErroresContenido] = useState({})
  const inputArchivoRef = useRef(null)

  useEffect(() => { cargarModulos() }, [workspaceId])

  const cargarModulos = async () => {
    if (!workspaceId) return
    setLoading(true)
    const { data } = await supabase.from('modulos_clase').select('*').eq('workspace_id', workspaceId).order('orden')
    setModulos(data || [])
    setLoading(false)
  }

  const abrirModulo = async (modulo) => {
    setModuloActivo(modulo)
    setLoadingContenido(true)
    setNota('')
    setNotaGuardada(false)

    const [{ data: cont }, { data: notaData }] = await Promise.all([
      supabase.from('contenido_clase').select('*').eq('modulo_id', modulo.id).order('orden'),
      supabase.from('notas_clase').select('contenido').eq('usuario_id', usuario.id).eq('modulo_id', modulo.id).single()
    ])

    setContenido(cont || [])
    setNota(notaData?.contenido || '')
    setLoadingContenido(false)
  }

  // Bloque 2.7: contenido_clase.url ya no se usa directo (bucket privado) —
  // se pide una signed URL fresca de corta duración recién al abrir el
  // recurso, vía la Edge Function (que reautoriza contra RLS/membresía real
  // del workspace en cada pedido, no solo al cargar el módulo).
  const abrirContenido = async (e, item) => {
    e.preventDefault()
    const { data, error } = await invocarFuncion('obtener-url-video', { body: { contenido_id: item.id } })
    if (error || !data?.url) { alert('No se pudo abrir el contenido. Probá de nuevo.'); return }
    window.open(data.url, '_blank', 'noopener,noreferrer')
  }

  const handleCambioNota = (valor) => {
    setNota(valor)
    setNotaGuardada(false)
    clearTimeout(autoGuardadoRef.current)
    autoGuardadoRef.current = setTimeout(() => guardarNota(valor), 1500)
  }

  const guardarNota = async (texto) => {
    if (!moduloActivo) return
    await supabase.from('notas_clase').upsert(
      { usuario_id: usuario.id, modulo_id: moduloActivo.id, contenido: texto, updated_at: new Date().toISOString() },
      { onConflict: 'usuario_id,modulo_id' }
    )
    setNotaGuardada(true)
  }

  // Admin: abrir modal de módulo (alta o edición)
  const abrirModalNuevoModulo = () => {
    setFormModulo(FORM_MODULO_VACIO)
    setModuloEditando(null)
    setErrorModulo(false)
    setModalModulo(true)
  }

  const abrirModalEditarModulo = (modulo) => {
    setFormModulo({ titulo: modulo.titulo, descripcion: modulo.descripcion || '' })
    setModuloEditando(modulo.id)
    setErrorModulo(false)
    setModalModulo(true)
  }

  // Admin: guardar módulo (alta o edición)
  const guardarModulo = async () => {
    if (!formModulo.titulo.trim()) { setErrorModulo(true); return }
    setGuardandoModulo(true)
    if (moduloEditando) {
      const payload = { titulo: formModulo.titulo.trim(), descripcion: formModulo.descripcion.trim() || null }
      const { data, error } = await supabase.from('modulos_clase').update(payload).eq('id', moduloEditando).select().single()
      if (error) { alert('No se pudo guardar el módulo.'); setGuardandoModulo(false); return }
      setModulos(prev => prev.map(m => m.id === moduloEditando ? data : m))
    } else {
      const maxOrden = modulos.length > 0 ? Math.max(...modulos.map(m => m.orden)) + 1 : 0
      const { data, error } = await supabase.from('modulos_clase').insert({
        titulo: formModulo.titulo.trim(),
        descripcion: formModulo.descripcion.trim() || null,
        orden: maxOrden,
        workspace_id: workspaceId
      }).select().single()
      if (error) { alert('No se pudo crear el módulo.'); setGuardandoModulo(false); return }
      setModulos(prev => [...prev, data])
    }
    setFormModulo(FORM_MODULO_VACIO)
    setModuloEditando(null)
    setModalModulo(false)
    setErrorModulo(false)
    setGuardandoModulo(false)
  }

  // Admin: eliminar módulo (cascada de contenido_clase — se avisa antes)
  const eliminarModulo = async (modulo) => {
    if (!window.confirm(`¿Eliminar "${modulo.titulo}"? Esto borra también todo su contenido.`)) return
    const { error } = await supabase.from('modulos_clase').delete().eq('id', modulo.id)
    if (error) { alert('No se pudo eliminar el módulo.'); return }
    setModulos(prev => prev.filter(m => m.id !== modulo.id))
  }

  // Admin: abrir modal de contenido (alta o edición)
  const abrirModalNuevoContenido = () => {
    setFormContenido(FORM_CONTENIDO_VACIO)
    setContenidoEditando(null)
    setErroresContenido({})
    setModalContenido(true)
  }

  const abrirModalEditarContenido = (item) => {
    setFormContenido({
      titulo: item.titulo, tipo: item.tipo, url: item.url,
      descripcion: item.descripcion || '', duracion_min: item.duracion_min || ''
    })
    setContenidoEditando(item.id)
    setErroresContenido({})
    setModalContenido(true)
  }

  // Admin: guardar contenido (alta o edición)
  const guardarContenido = async () => {
    const errores = {}
    if (!formContenido.titulo.trim()) errores.titulo = true
    if (!formContenido.url.trim()) errores.url = true
    if (Object.keys(errores).length > 0) { setErroresContenido(errores); return }

    setGuardandoContenido(true)
    const payload = {
      tipo: formContenido.tipo,
      titulo: formContenido.titulo.trim(),
      url: formContenido.url.trim(),
      descripcion: formContenido.descripcion.trim() || null,
      duracion_min: formContenido.duracion_min ? parseInt(formContenido.duracion_min) : null
    }
    if (contenidoEditando) {
      const { data, error } = await supabase.from('contenido_clase').update(payload).eq('id', contenidoEditando).select().single()
      if (error) { alert('No se pudo guardar el contenido.'); setGuardandoContenido(false); return }
      setContenido(prev => prev.map(c => c.id === contenidoEditando ? data : c))
    } else {
      const maxOrden = contenido.length > 0 ? Math.max(...contenido.map(c => c.orden)) + 1 : 0
      const { data, error } = await supabase.from('contenido_clase').insert({
        ...payload, modulo_id: moduloActivo.id, orden: maxOrden
      }).select().single()
      if (error) { alert('No se pudo guardar el contenido.'); setGuardandoContenido(false); return }
      setContenido(prev => [...prev, data])
    }
    setFormContenido(FORM_CONTENIDO_VACIO)
    setContenidoEditando(null)
    setErroresContenido({})
    setModalContenido(false)
    setModuloActivo(null)
    setGuardandoContenido(false)
  }

  // Admin: eliminar contenido
  const eliminarContenido = async (item) => {
    const { error } = await supabase.from('contenido_clase').delete().eq('id', item.id)
    if (error) { alert('No se pudo eliminar el contenido.'); return }
    setContenido(prev => prev.filter(c => c.id !== item.id))
  }

  // Admin: subir archivo a Storage y usar URL
  const subirArchivo = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    setSubiendoArchivo(true)
    const ext = file.name.split('.').pop()
    const path = `${moduloActivo.id}/${Date.now()}.${ext}`
    const { error } = await supabase.storage.from('contenido-clases').upload(path, file)
    setSubiendoArchivo(false)
    if (error) { alert('No se pudo subir el archivo. Probá de nuevo.'); return }
    const { data: { publicUrl } } = supabase.storage.from('contenido-clases').getPublicUrl(path)
    setFormContenido(f => ({ ...f, url: publicUrl }))
  }

  if (loading) return <div className="loading">Cargando clases...</div>

  // Vista módulo
  if (moduloActivo) {
    return (
      <div className="clases-page">
        <div className="clases-modulo-header">
          <button className="clases-back" onClick={() => setModuloActivo(null)}>← Módulos</button>
          <h2 className="clases-modulo-titulo">{moduloActivo.titulo}</h2>
          {moduloActivo.descripcion && <p className="clases-modulo-desc">{moduloActivo.descripcion}</p>}
        </div>

        {loadingContenido ? (
          <div className="loading">Cargando contenido...</div>
        ) : (
          <div className="clases-modulo-cuerpo">
            {esAdmin ? (
              <PanelAdminCRUD
                formulario={<button className="btn-primario" onClick={abrirModalNuevoContenido}>+ Agregar contenido</button>}
                columnas={COLUMNAS_CONTENIDO}
                filas={contenido}
                onEditar={abrirModalEditarContenido}
                onEliminar={eliminarContenido}
                vacioTexto="Todavía no hay contenido en este módulo."
              />
            ) : contenido.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📭</div>
                <p>Aún no hay contenido en este módulo.</p>
              </div>
            ) : (
              <div className="clases-lista-contenido">
                {contenido.map(item => (
                  <a key={item.id} href="#" onClick={e => abrirContenido(e, item)} className="clases-item">
                    <span className="clases-item-icono">{TIPO_ICONOS[item.tipo]}</span>
                    <div className="clases-item-info">
                      <p className="clases-item-titulo">{item.titulo}</p>
                      {item.descripcion && <p className="clases-item-desc">{item.descripcion}</p>}
                      <div className="clases-item-meta">
                        <span className="clases-item-tipo">{TIPO_LABELS[item.tipo]}</span>
                        {item.duracion_min && <span>{item.duracion_min} min</span>}
                      </div>
                    </div>
                    <span className="clases-item-flecha">›</span>
                  </a>
                ))}
              </div>
            )}

            {/* NOTAS */}
            <div className="clases-notas-seccion">
              <div className="clases-notas-header">
                <p className="clases-notas-label">Mis notas de este módulo</p>
                {notaGuardada && <span className="clases-guardado">✓ Guardado</span>}
              </div>
              <p className="clases-notas-aclaracion">Nota personal — solo la ves vos, no se comparte con nadie.</p>
              <textarea
                className="clases-notas-textarea"
                placeholder="Escribí lo que te resonó, preguntas, ideas para aplicar..."
                value={nota}
                onChange={e => handleCambioNota(e.target.value)}
                rows={3}
              />
              <button className="btn-secundario" onClick={() => guardarNota(nota)}>
                Guardar nota
              </button>
            </div>
          </div>
        )}

        {/* MODAL CONTENIDO (admin) */}
        {modalContenido && esAdmin && (
          <div className="modal-overlay" onClick={() => setModalContenido(false)}>
            <div className="modal-card modal-card-scroll" onClick={e => e.stopPropagation()}>
              <h3 className="modal-titulo">{contenidoEditando ? 'Editar contenido' : 'Nuevo contenido'}</h3>
              <p className="clases-form-ayuda">
                Pegá un link ya alojado (YouTube, Vimeo, Google Drive...) — tiene que ser público o "no listado", uno privado no va a andar para el paciente. O subí el archivo directo (PDF, audio o video) desde tu compu o celular; si es muy pesado puede tardar más en subir.
              </p>
              <div className="progreso-form">
                <label className="form-label label-requerido">Título</label>
                <input className={`form-input ${erroresContenido.titulo ? 'input-error' : ''}`} placeholder="Nombre del recurso"
                  value={formContenido.titulo} onChange={e => setFormContenido(f => ({ ...f, titulo: e.target.value }))} />
                {erroresContenido.titulo && <p className="campo-error-msg">Falta el título.</p>}
                <label className="form-label">Tipo</label>
                <div className="clases-tipo-btns">
                  {['video', 'pdf', 'audio'].map(t => (
                    <button key={t}
                      className={`clases-tipo-btn ${formContenido.tipo === t ? 'active' : ''}`}
                      onClick={() => setFormContenido(f => ({ ...f, tipo: t }))}>
                      {TIPO_ICONOS[t]} {TIPO_LABELS[t]}
                    </button>
                  ))}
                </div>
                <label className="form-label label-requerido">URL (o subir archivo)</label>
                <input className={`form-input ${erroresContenido.url ? 'input-error' : ''}`} placeholder="https://..."
                  value={formContenido.url} onChange={e => setFormContenido(f => ({ ...f, url: e.target.value }))} />
                {erroresContenido.url && <p className="campo-error-msg">Falta la URL, o subí un archivo abajo.</p>}
                <button className="form-btn-foto" onClick={() => inputArchivoRef.current?.click()} disabled={subiendoArchivo}>
                  {subiendoArchivo ? '⏳ Subiendo...' : '📤 Subir archivo a Storage'}
                </button>
                <input ref={inputArchivoRef} type="file" style={{ display: 'none' }} onChange={subirArchivo} />
                <label className="form-label">Duración (min) — opcional</label>
                <input className="form-input" type="number" placeholder="—"
                  value={formContenido.duracion_min} onChange={e => setFormContenido(f => ({ ...f, duracion_min: e.target.value }))} />
                <label className="form-label">Descripción — opcional</label>
                <input className="form-input" placeholder="Descripción breve"
                  value={formContenido.descripcion} onChange={e => setFormContenido(f => ({ ...f, descripcion: e.target.value }))} />
              </div>
              <div className="modal-actions">
                <button className="btn-secundario" onClick={() => setModalContenido(false)} disabled={guardandoContenido}>Cancelar</button>
                <button className="btn-primario" onClick={guardarContenido} disabled={guardandoContenido}>
                  {guardandoContenido ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Vista lista de módulos
  return (
    <div className="clases-page">
      <div className="page-header">
        <h1 className="page-titulo">Clases</h1>
        <p className="page-subtitulo">Módulos y recursos del programa</p>
      </div>

      <div className="clases-contenido">
        {esAdmin ? (
          <PanelAdminCRUD
            formulario={<button className="btn-primario" onClick={abrirModalNuevoModulo}>+ Nuevo módulo</button>}
            columnas={COLUMNAS_MODULOS}
            filas={modulos}
            onFilaClick={abrirModulo}
            onEditar={abrirModalEditarModulo}
            onEliminar={eliminarModulo}
            vacioTexto="Todavía no hay módulos. Creá el primero."
          />
        ) : modulos.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📚</div>
            <p>Próximamente habrá clases disponibles.</p>
          </div>
        ) : (
          <div className="clases-lista-modulos">
            {modulos.map((m, i) => (
              <div key={m.id} className="clases-modulo-card" onClick={() => abrirModulo(m)}>
                <div className="clases-modulo-numero">
                  <span>{i + 1}</span>
                </div>
                <div className="clases-modulo-info">
                  <p className="clases-modulo-nombre">{m.titulo}</p>
                  {m.descripcion && <p className="clases-modulo-desc-preview">{m.descripcion}</p>}
                </div>
                <span className="clases-modulo-flecha">›</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MODAL MÓDULO (admin) */}
      {modalModulo && esAdmin && (
        <div className="modal-overlay" onClick={() => setModalModulo(false)}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <h3 className="modal-titulo">{moduloEditando ? 'Editar módulo' : 'Nuevo módulo'}</h3>
            <div className="progreso-form">
              <label className="form-label label-requerido">Título del módulo</label>
              <input className={`form-input ${errorModulo ? 'input-error' : ''}`} placeholder="Ej: Módulo 1, Alimentación consciente"
                value={formModulo.titulo} onChange={e => setFormModulo(f => ({ ...f, titulo: e.target.value }))} />
              {errorModulo && <p className="campo-error-msg">Falta el título.</p>}
              <label className="form-label">Descripción — opcional</label>
              <textarea className="form-input" rows={3} placeholder="¿De qué trata este módulo?"
                value={formModulo.descripcion} onChange={e => setFormModulo(f => ({ ...f, descripcion: e.target.value }))} />
            </div>
            <div className="modal-actions">
              <button className="btn-secundario" onClick={() => setModalModulo(false)} disabled={guardandoModulo}>Cancelar</button>
              <button className="btn-primario" onClick={guardarModulo} disabled={guardandoModulo}>
                {guardandoModulo ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
